import { FormArray, FormGroup } from '@angular/forms';

import type {
    PendingDropdownItem,
    RepeatablePendingDropdownCascadePassStats,
    RepeatablePendingDropdownResolveOptions,
    RepeatablePendingDropdownResolutionPolicy,
    RepeatablePendingDropdownResolutionSummary,
    RepeatablePendingDropdownResolutionTarget,
    RepeatableResolvedDependencyChange,
} from './repeatable-group.types';
import { RepeatablePendingDropdownResolver } from './repeatable-pending-dropdown.resolver';

export type RepeatablePendingDropdownCascadeDependencyResolver = (
    changes: RepeatableResolvedDependencyChange[],
) => Promise<boolean>;

export type RepeatablePendingDropdownOrchestrationOptions = {
    rowsArray: FormArray<FormGroup>;
    pendingDropdown: PendingDropdownItem[];
    policy: RepeatablePendingDropdownResolutionPolicy;
    trigger: string;
    attempt: number;
    stalledAttemptCount: number;
    targets?: readonly RepeatablePendingDropdownResolutionTarget[];
    startedAt?: number;
    resolveDependencies: RepeatablePendingDropdownCascadeDependencyResolver;
};

export type RepeatablePendingDropdownOrchestrationResult = {
    pendingDropdown: PendingDropdownItem[];
    summary: RepeatablePendingDropdownResolutionSummary;
    cancelled: boolean;
    nextStalledAttemptCount: number;
    shouldScheduleRetry: boolean;
};

export class RepeatablePendingDropdownOrchestrator {
    public constructor(private readonly resolver: RepeatablePendingDropdownResolver) {}

    public async resolve(
        options: RepeatablePendingDropdownOrchestrationOptions,
    ): Promise<RepeatablePendingDropdownOrchestrationResult> {
        const startedAt = options.startedAt ?? Date.now();
        const pendingBeforeCount = options.pendingDropdown.length;
        const targets = [...(options.targets ?? [])];
        const firstPass = this.resolver.resolvePending(options.rowsArray, options.pendingDropdown, this.createResolveOptions(targets));
        let pendingDropdown = firstPass.stillPending;
        let resolvedChanges = firstPass.resolvedChanges;
        let cascadePass = 0;
        const cascadePasses: RepeatablePendingDropdownCascadePassStats[] = [];
        let cancelled = false;

        while (resolvedChanges.length > 0 && cascadePass < options.policy.cascadePassLimit) {
            cascadePass++;

            const dependencyChanges = resolvedChanges;
            const shouldContinue = await options.resolveDependencies(dependencyChanges);

            if (!shouldContinue) {
                cancelled = true;
                break;
            }

            const retryResult = this.resolver.resolvePending(options.rowsArray, pendingDropdown);

            pendingDropdown = retryResult.stillPending;
            resolvedChanges = retryResult.resolvedChanges;
            cascadePasses.push({
                pass: cascadePass,
                dependencyChangeCount: dependencyChanges.length,
                resolvedChanges: dependencyChanges,
                result: retryResult.stats,
            });
        }

        const pendingCountChanged = pendingBeforeCount !== pendingDropdown.length;
        const madeProgress = this.hasResolutionProgress(firstPass.stats, cascadePasses) || pendingCountChanged;
        const nextStalledAttemptCount = madeProgress ? 0 : options.stalledAttemptCount + 1;
        const reachedAttemptLimit = pendingDropdown.length > 0 && options.attempt >= options.policy.maxAttempts;
        const reachedCascadeLimit = resolvedChanges.length > 0 && cascadePass >= options.policy.cascadePassLimit;
        const reachedStalledAttemptLimit = pendingDropdown.length > 0 &&
            nextStalledAttemptCount >= options.policy.stalledAttemptLimit;
        const shouldScheduleRetry = pendingDropdown.length > 0 &&
            !reachedAttemptLimit &&
            !reachedStalledAttemptLimit;

        return {
            pendingDropdown,
            cancelled,
            nextStalledAttemptCount,
            shouldScheduleRetry,
            summary: {
                trigger: options.trigger,
                targets,
                attempt: options.attempt,
                pendingBeforeCount,
                pendingAfterCount: pendingDropdown.length,
                maxAttempts: options.policy.maxAttempts,
                retryDelayMs: options.policy.retryDelayMs,
                cascadePassLimit: options.policy.cascadePassLimit,
                stalledAttemptLimit: options.policy.stalledAttemptLimit,
                stalledAttemptCount: nextStalledAttemptCount,
                madeProgress,
                pendingCountChanged,
                shouldScheduleRetry,
                reachedAttemptLimit,
                reachedCascadeLimit,
                reachedStalledAttemptLimit,
                startedAt,
                finishedAt: Date.now(),
                firstPass: firstPass.stats,
                cascadePasses,
            },
        };
    }


    private hasResolutionProgress(
        firstPass: RepeatablePendingDropdownResolutionSummary['firstPass'],
        cascadePasses: RepeatablePendingDropdownCascadePassStats[],
    ): boolean {
        if (firstPass.resolvedValueCount > 0 || firstPass.missingControlCount > 0) {
            return true;
        }

        return cascadePasses.some((pass) =>
            pass.result.resolvedValueCount > 0 || pass.result.missingControlCount > 0);
    }

    private createResolveOptions(
        targets: RepeatablePendingDropdownResolutionTarget[],
    ): RepeatablePendingDropdownResolveOptions {
        if (targets.length === 0) {
            return {};
        }

        return { targets };
    }
}
